// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useState, useEffect, Fragment, useRef } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { withStyles } from '@material-ui/styles';
import { get } from 'lodash';
import { push } from 'connected-react-router';
import { arrayOf, bool, func, shape } from 'prop-types';

import {
  Footer, TabPanel, TabsContainer,
} from '../../../components';
import { getTabIndex, hasAdminRole } from '../../../utils';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';

import { PROCEED_TEXT, ALERT_SUCCESS } from '../../../constants';
import {
  fetchKeyStore,
  deleteKeyStore,
} from '../../../actions/keyStore';
import {
  getIsError,
  getErrors,
  getIsLoading,
  getKeyStoreDetails,
  getIsKeyStoreSaveSuccess,
  getIsKeyStoreDeleteSuccess,
} from '../../../reducers/keyStore';
import {
  getConfig,
  getUserDetails,
  getFeatureFlagForKeyStore,
} from '../../../reducers/portalConfig';
import { DetailsHeader, StatusIndicator } from '../../../components';
import DetailsContainer from '../../details';
import { hasError } from '../../../utils';
import Overview from './Overview';
import Proxies from './Proxies';
import KeyDeployments from './KeyDeployments';
import styles from './styles';

const getTabItems = (uuid) => [
  {
    id: 'overview',
    tabId: 'key-store-details-overview-tab',
    label: getI18nFormattedMessage('label.overview'),
    visible: true,
  },
  {
    id: 'proxies',
    tabId: 'key-store-details-proxies-tab',
    label: getI18nFormattedMessage('label.key.store.details.proxies'),
    visible: !!uuid,
  },
  {
    id: 'key-deployments',
    tabId: 'key-store-details-key-deployments-tab',
    label: getI18nFormattedMessage('label.key.store.details.key.deployments'),
    visible: !!uuid,
  },
];

const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);

export const KeyStoreDetails = (props) => {
  const {
    isLoading,
    userContext,
    pageErrors,
    isError,
    keyStoreDetails,
    featureFlagKeyStore,
    classes,
    history,
    isKeyStoreSaveSuccess,
    isKeyStoreDeleteSuccess,
  } = props;

  const uuid = get(props, 'match.params.uuid');
  const tabItems = getTabItems(uuid);

  const pageHeaderRef = useRef(null);
  const intl = getI18n(useIntl());
  const [name, setName] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFieldEdit, setIsFieldEdit] = useState(!uuid);
  const [showDelete, setShowDelete] = useState(!uuid);
  const [isFieldEditIcon, setIsFieldEditIcon] = useState(!uuid);
  const [isFieldEditName, setIsFieldEditName] = useState(false);
  const [isNameError, setIsNameError] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [errors, setErrors] = useState([]);
  const tabs = getTabs(tabItems);
  const defaultIndex = getTabIndex(tabs, props.match);
  const [redirectTabValue, setRedirectTabValue] = useState(tabs[defaultIndex].tabId);

  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(tabs[defaultIndex].tabId);

  useEffect(() => {
    window.scrollTo(0, pageHeaderRef.offsetTop);
    //if (!featureFlagKeyStore) { props.push('/404'); }
  }, [featureFlagKeyStore]);

  useEffect(() => {
    if (isKeyStoreDeleteSuccess) {
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.key.store.delete.success'));
      history.push('/admin/key-stores');
    }
  }, [isKeyStoreDeleteSuccess]);

  useEffect(() => {
    localStorage.setItem('isUnSavedChanges-key-store', false);
    if (!hasAdminRole(userContext)) { props.push('/404'); }
  }, [userContext]);

  useEffect(() => {
    if (isKeyStoreSaveSuccess || uuid) {
      setIsFieldEdit(false);
      setIsFieldEditName(false);
      setShowDelete(true);
      if(uuid) {
        props.fetchKeyStore(uuid);
      }
      if(isKeyStoreSaveSuccess) {
        setTimeout(() => { notifyMessages('', '')}, 2500);
      }
    }
  }, [isKeyStoreSaveSuccess, uuid]);


  useEffect(() => {
    if(keyStoreDetails && keyStoreDetails.name) {
      setName(keyStoreDetails.name);
    }
  }, [keyStoreDetails]);

  useEffect(() => {
     const defaultIndex = getTabIndex(tabs, props.match);
      setTabIndex(defaultIndex);
      setCurrentTab(tabs[defaultIndex].tabId);
      if(tabs[defaultIndex].tabId === 'key-store-details-overview-tab') {
        setIsFieldEditIcon(true);
      } else {
        setIsFieldEditIcon(false);
      }
  }, [props.match]);

  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };

  useEffect(() => {
    setIsFieldEditName(false);
    setErrors(isError ? pageErrors : []);
  }, [isError, pageErrors]);

  const handleChange = (value) => {
    localStorage.setItem('isUnSavedChanges-key-store', true);
    const nameUiError = hasError(value, true);
    if(uuid && get(keyStoreDetails, 'name') === value) {
      setIsFieldEditName(false);
    } else {
      setIsFieldEditName(true);
    }
    setName(value);
    setIsNameError(nameUiError);
  }

  const onClickEditIcon = () => setIsFieldEdit(true);

  const deleteFn = () => {
    props.deleteKeyStore(uuid);
  };

  const onHideEditTitle = () => {
    setIsFieldEditName(false);
    setName(get(keyStoreDetails, 'name'));
    setIsFieldEdit(false);
  }

  const handleTabChange = (e, newValue) => {
    const hasUnsavedChanges = localStorage.getItem('isUnSavedChanges-key-store');
    if (hasUnsavedChanges === 'true') {
      setIsDialogOpen(true);
      setRedirectTabValue(newValue);
    } else {
      if(tabs[newValue].tabId !== 'key-store-details-overview-tab') {
        setIsFieldEditIcon(false);
        onHideEditTitle();
      }
      setTabIndex(newValue);
      setCurrentTab(tabs[newValue].tabId);
      history.push(`/admin/key-stores/details/${uuid}/${tabs[newValue].id}`)
    }
  };

  const onDialogClose = () => {
    setIsDialogOpen(false);
  };

  const onDialogSubmit = () => {
    onHideEditTitle();
    localStorage.setItem('isUnSavedChanges-key-store', false);
    setIsDialogOpen(false);
    setTabIndex(redirectTabValue);
    setCurrentTab(tabs[redirectTabValue].tabId);
    history.push(`/admin/key-stores/details/${uuid}/${tabs[redirectTabValue].id}`)
  };

  const Status = () =>
  <StatusIndicator
    status={get(keyStoreDetails, 'connectionStatus')}
  />;


  return (
    <DetailsContainer
      detailsPageId="keyStore-details-page"
      isLoading={isLoading}
      dialogId="key-store-unsaved-dialog"
      isDialogOpen={isDialogOpen}
      onDialogClose={onDialogClose}
      dialogSubmitText={PROCEED_TEXT}
      onDialogSubmit={onDialogSubmit}
      notificationId="key-store-details-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      notificationContainerClass={classes.notificationContainer}
      hasNotificationIcon={true}
      isActionsEnabled={false}
      errors={errors}
      pageHeaderRef={pageHeaderRef}
      header={
        <DetailsHeader
          headerId="key-store-details-header-container"
          title={intl.getI18nMessage('label.key.store')}
          type="textField"
          handleChange={handleChange}
          name={name}
          helperText={intl.getI18nMessage('label.key.store.name.help')}
          isNameError={isNameError}
          isFieldEdit={isFieldEdit}
          isFieldEditIcon={isFieldEditIcon}
          onClickEditIcon={onClickEditIcon}
          deleteFn={deleteFn}
          showDelete={uuid && showDelete}
          statusComponent={<Status />}
        />
      }
      tabs={
        <TabsContainer
          id={'key-store-details-tab-container'}
          tabValue={tabIndex}
          orientation="horizontal"
          tabItems={tabs}
          handleTabChange={handleTabChange}
        />
      }
      page={
        <Fragment>
          <TabPanel
            visible={currentTab === 'key-store-details-overview-tab'}
            id="key-store-overview-tab-panel"
          >
            <Overview
              {...props}
              isCurrentTab={currentTab === 'key-store-details-overview-tab'}
              uuid={uuid}
              notifyMessages={notifyMessages}
              keyStoreName={name}
              keyStoreDetails={keyStoreDetails}
              isFieldEditName={isFieldEditName}
              onHideEditTitle={onHideEditTitle}
            />
          </TabPanel>
          <TabPanel
            visible={currentTab === 'key-store-details-proxies-tab'}
            id="key-store-details-proxies-tab-panel"
          >
            <Proxies
              {...props}
              uuid={uuid}
              isCurrentTab={currentTab === 'key-store-details-proxies-tab'}
              notifyMessages={notifyMessages}
            />
          </TabPanel>
          <TabPanel
            visible={currentTab === 'key-store-details-key-deployments-tab'}
            id="keyStore-details-apis-tab-panel"
          >
            <KeyDeployments
              {...props}
              isCurrentTab={currentTab === 'key-store-details-key-deployments-tab'}
              uuid={uuid}
              notifyMessages={notifyMessages}
            />
          </TabPanel>
        </Fragment>
      }
      footer={
        <Footer />
      }
    />
  );
};

const mapStateToProps = state => ({
  config: getConfig(state),
  isLoading: getIsLoading(state),
  isError: getIsError(state),
  pageErrors: getErrors(state),
  userContext: getUserDetails(state),
  keyStoreDetails: getKeyStoreDetails(state),
  featureFlagKeyStore: getFeatureFlagForKeyStore(state),
  isKeyStoreSaveSuccess: getIsKeyStoreSaveSuccess(state),
  isKeyStoreDeleteSuccess: getIsKeyStoreDeleteSuccess(state),
});

const mapDispatchToProps = {
  fetchKeyStore,
  push,
  deleteKeyStore,
};

KeyStoreDetails.propTypes = {
  config: shape({}),
  userContext: shape({}),
  history: shape({}),
  isLoading: bool,
  isError: bool,
  pageErrors: arrayOf(shape({})),
  match: shape({}),
  push: func,
  keyStoreDetails: shape({}),
  featureFlagKeyStore: bool,
  isKeyStoreSaveSuccess: bool,
  classes: shape({}),
  fetchKeyStore: func,
  isKeyStoreDeleteSuccess: bool,
  deleteKeyStore: func,
};

KeyStoreDetails.displayName = 'KeyStoreDetails';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(KeyStoreDetails);
